import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Statistic, Table, Typography, Spin, message,
  Space, Layout, Tag, Button, Select,
} from 'antd';
import { FilePdfOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import apiService from '../../services/api';
import RunDateFilter from '../../components/RunDateFilter';

const { Option } = Select;
const { Title, Text } = Typography;
const { Content } = Layout;


const fmtShort = (v) => `£${Number(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const DailyRunBreakdown = () => {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState([dayjs().startOf('month'), dayjs()]);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  useEffect(() => {
    apiService.opcClients.getAll().then(res => {
      setClients(Array.isArray(res.data) ? res.data : []);
    }).catch(() => {});
  }, []);

  const handleFilterChange = (range) => {
    if (range) setDateRange(range);
  };

  const handleGenerateInvoice = async () => {
    if (!selectedClient) { message.warning('Please select a client first'); return; }
    try {
      setInvoiceLoading(true);
      const res = await apiService.dailyRuns.invoicePDF({
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD'),
        clientName: selectedClient,
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${selectedClient.replace(/\s+/g, '-')}-${dateRange[0].format('YYYY-MM')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      message.error('Failed to generate invoice');
    } finally {
      setInvoiceLoading(false);
    }
  };
  const [data, setData] = useState({
    byClient: [],
    byVan: [],
    driverActivity: [],
    ownVsCovered: { own: { jobs: 0, earnings: 0 }, covered: { jobs: 0, earnings: 0 } },
  });

  const fetchData = useCallback(async (range) => {
    if (!range || !range[0] || !range[1]) return;
    try {
      setLoading(true);
      const res = await apiService.dailyRuns.getOverview({
        startDate: range[0].format('YYYY-MM-DD'),
        endDate: range[1].format('YYYY-MM-DD'),
      });
      setData(res.data);
    } catch {
      message.error('Failed to load breakdown data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(dateRange);
  }, [fetchData, dateRange]);

  const { byClient, byVan, driverActivity, ownVsCovered, fuel = { total: 0, byDriver: [] } } = data;

  const tableProps = { pagination: false, size: 'small', bordered: true };

  const driverColumns = [
    { title: 'Driver', dataIndex: 'name', key: 'name' },
    { title: 'Days Worked', dataIndex: 'daysWorked', key: 'daysWorked', sorter: (a, b) => a.daysWorked - b.daysWorked, align: 'center' },
    { title: 'Jobs', dataIndex: 'jobs', key: 'jobs', sorter: (a, b) => a.jobs - b.jobs, align: 'center' },
    {
      title: 'Earnings', dataIndex: 'earnings', key: 'earnings',
      sorter: (a, b) => a.earnings - b.earnings, align: 'right',
      render: (v, row) => row.name === 'Lite' ? (
        <span>{fmtShort(v)} <Tag color="orange" style={{ fontSize: 10, marginLeft: 4 }}>15%</Tag></span>
      ) : fmtShort(v),
    },
  ];

  const clientColumns = [
    { title: 'Client', dataIndex: 'name', key: 'name' },
    { title: 'Jobs', dataIndex: 'jobs', key: 'jobs', sorter: (a, b) => a.jobs - b.jobs, align: 'center' },
    { title: 'Earnings', dataIndex: 'earnings', key: 'earnings', sorter: (a, b) => a.earnings - b.earnings, align: 'right', render: fmtShort },
  ];

  const vanColumns = [
    { title: 'Van', dataIndex: 'name', key: 'name' },
    { title: 'Jobs', dataIndex: 'jobs', key: 'jobs', sorter: (a, b) => a.jobs - b.jobs, align: 'center' },
    { title: 'Earnings', dataIndex: 'earnings', key: 'earnings', sorter: (a, b) => a.earnings - b.earnings, align: 'right', render: fmtShort },
  ];

  const tableSummary2 = (rows) => {
    const totalJobs = rows.reduce((s, r) => s + r.jobs, 0);
    const totalEarnings = rows.reduce((s, r) => s + r.earnings, 0);
    return (
      <Table.Summary.Row style={{ fontWeight: 600 }}>
        <Table.Summary.Cell>Total</Table.Summary.Cell>
        <Table.Summary.Cell align="center">{totalJobs}</Table.Summary.Cell>
        <Table.Summary.Cell align="right">{fmtShort(totalEarnings)}</Table.Summary.Cell>
      </Table.Summary.Row>
    );
  };

  const driverSummary = (rows) => {
    const totalJobs = rows.reduce((s, r) => s + r.jobs, 0);
    const totalEarnings = rows.reduce((s, r) => s + r.earnings, 0);
    return (
      <Table.Summary.Row style={{ fontWeight: 600 }}>
        <Table.Summary.Cell>Total</Table.Summary.Cell>
        <Table.Summary.Cell />
        <Table.Summary.Cell align="center">{totalJobs}</Table.Summary.Cell>
        <Table.Summary.Cell align="right">{fmtShort(totalEarnings)}</Table.Summary.Cell>
      </Table.Summary.Row>
    );
  };

  return (
    <Layout>
      <Content style={{ padding: '24px' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Title level={2} style={{ marginBottom: 4 }}>Breakdown</Title>
            <Text type="secondary">Driver activity, client and van performance for the selected period.</Text>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <RunDateFilter onChange={handleFilterChange} />
            <Select
              placeholder="Select client"
              style={{ width: 180 }}
              allowClear
              value={selectedClient}
              onChange={setSelectedClient}
              showSearch
            >
              {clients.map(c => <Option key={c._id} value={c.name}>{c.name}</Option>)}
            </Select>
            <Button
              type="primary"
              icon={<FilePdfOutlined />}
              loading={invoiceLoading}
              onClick={handleGenerateInvoice}
            >
              Generate Invoice
            </Button>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 60 }}>
              <Spin size="large" />
            </div>
          ) : (
            <>
              {/* Own vs Covered */}
              <Card title="Own vs Covered">
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <Statistic title="Own Driver Jobs" value={ownVsCovered.own.jobs} />
                      <div style={{ marginTop: 8, fontSize: 18, fontWeight: 600, color: '#3f8600' }}>
                        {fmtShort(ownVsCovered.own.earnings)}
                      </div>
                      <Text type="secondary" style={{ fontSize: 12 }}>full rate</Text>
                    </Card>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <Statistic title="Covered by Lite" value={ownVsCovered.covered.jobs} />
                      <div style={{ marginTop: 8, fontSize: 18, fontWeight: 600, color: '#fa8c16' }}>
                        {fmtShort(ownVsCovered.covered.earnings)}
                      </div>
                      <Text type="secondary" style={{ fontSize: 12 }}>15% of gross</Text>
                    </Card>
                  </Col>
                </Row>
              </Card>

              {/* Driver Activity */}
              <Card title="Driver Activity">
                <Table
                  {...tableProps}
                  columns={driverColumns}
                  dataSource={driverActivity}
                  rowKey="name"
                  summary={driverSummary}
                />
              </Card>

              {/* Fuel by Driver */}
              <Card
                title="Fuel by Driver"
                extra={
                  fuel.total > 0 && (
                    <Text style={{ color: '#ff7a45', fontWeight: 600 }}>
                      Total: £{fuel.total.toFixed(2)}
                    </Text>
                  )
                }
              >
                <Table
                  {...tableProps}
                  rowKey="name"
                  dataSource={fuel.byDriver}
                  locale={{ emptyText: 'No fuel entries for this period' }}
                  columns={[
                    { title: 'Driver', dataIndex: 'name', key: 'name' },
                    { title: 'Fill-ups', dataIndex: 'entries', key: 'entries', width: 90, align: 'center' },
                    {
                      title: 'Amount', dataIndex: 'amount', key: 'amount', width: 120, align: 'right',
                      sorter: (a, b) => a.amount - b.amount,
                      render: (v) => <Text strong style={{ color: '#ff7a45' }}>£{v.toFixed(2)}</Text>,
                    },
                  ]}
                  summary={(rows) => {
                    if (!rows.length) return null;
                    return (
                      <Table.Summary.Row style={{ fontWeight: 600 }}>
                        <Table.Summary.Cell>Total</Table.Summary.Cell>
                        <Table.Summary.Cell align="center">{rows.reduce((s, r) => s + r.entries, 0)}</Table.Summary.Cell>
                        <Table.Summary.Cell align="right">
                          £{rows.reduce((s, r) => s + r.amount, 0).toFixed(2)}
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    );
                  }}
                />
              </Card>

              {/* By Client + By Van */}
              <Row gutter={[16, 16]}>
                <Col xs={24} xl={12}>
                  <Card title="By Client">
                    <Table
                      {...tableProps}
                      columns={clientColumns}
                      dataSource={byClient}
                      rowKey="name"
                      summary={tableSummary2}
                    />
                  </Card>
                </Col>
                <Col xs={24} xl={12}>
                  <Card title="By Van">
                    <Table
                      {...tableProps}
                      columns={vanColumns}
                      dataSource={byVan}
                      rowKey="name"
                      summary={tableSummary2}
                    />
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </Space>
      </Content>
    </Layout>
  );
};

export default DailyRunBreakdown;
